import { Button, Group, Loader, Modal, SimpleGrid, Stack, TextInput } from "@mantine/core";
import {
  fieldStyles,
  modalStyles,
  MODAL_MAX_WIDTH,
  primaryBtnStyles,
  secondaryBtnStyles,
  useIsMobile,
} from "./formStyles";
import { useEffect, useState } from "react";
import { FiChevronLeft, FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import styled from "styled-components";
import { PersonService } from "../../services/person/person.service";
import { Person, UnitService } from "../../services/unit/unit.service";
import { PageHeader, PageWrapper } from "./styles";

const notify = (msg: string): void => {
  toast.warning(msg, { position: toast.POSITION.TOP_RIGHT });
};
const notifyOk = (msg: string): void => {
  toast.success(msg, { position: toast.POSITION.TOP_RIGHT });
};

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  date_of_birth: "",
  date_baptism: "",
};

const isoToDateInput = (iso?: string): string =>
  iso ? iso.slice(0, 10) : "";

const formatDate = (iso?: string): string => {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

export function AlunosUnidade() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { unitId } = useParams<{ unitId: string }>();
  const location = useLocation();
  const unitName = (location.state as { unitName?: string })?.unitName ?? "";

  const [people, setPeople] = useState<Person[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Person | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [isSaving, setIsSaving] = useState(false);

  const fetch = async (): Promise<void> => {
    if (!unitId) return;
    setIsLoading(true);
    try {
      const res = await UnitService.getPeople(unitId);
      setPeople(res);
    } catch (error: any) {
      notify(error[0]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitId]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setModalOpen(true);
  };

  const openEdit = (person: Person) => {
    setEditing(person);
    setForm({
      name: person.name ?? "",
      email: person.email ?? "",
      phone: person.phone ?? "",
      date_of_birth: isoToDateInput(person.date_of_birth),
      date_baptism: isoToDateInput(person.date_baptism),
    });
    setModalOpen(true);
  };

  const setField = (key: keyof typeof emptyForm, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async (): Promise<void> => {
    if (!unitId) return;
    if (!form.name.trim()) return notify("Informe o nome do aluno");

    const payload = {
      unit_id: unitId,
      name: form.name,
      email: form.email,
      phone: form.phone,
      date_of_birth: form.date_of_birth,
      date_baptism: form.date_baptism,
    };

    setIsSaving(true);
    try {
      if (editing) {
        await PersonService.update(editing.id, payload);
        notifyOk("Aluno atualizado");
      } else {
        await PersonService.create(payload);
        notifyOk("Aluno cadastrado");
      }
      setModalOpen(false);
      await fetch();
    } catch (error: any) {
      notify(error[0]);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (person: Person): Promise<void> => {
    if (!window.confirm(`Excluir o aluno "${person.name}"?`)) return;
    try {
      await PersonService.remove(person.id);
      notifyOk("Aluno excluído");
      await fetch();
    } catch (error: any) {
      notify(error[0]);
    }
  };

  return (
    <PageWrapper>
      <PageHeader onClick={() => navigate("/unidades")}>
        <FiChevronLeft />
        <span>Alunos{unitName ? ` — ${unitName}` : ""}</span>
      </PageHeader>

      <Content>
        <Toolbar>
          <PrimaryButton onClick={openCreate}>
            <FiPlus />
            <span>Novo aluno</span>
          </PrimaryButton>
        </Toolbar>

        <Panel>
          {isLoading ? (
            <Centered>
              <Loader color="#3439a5" />
            </Centered>
          ) : people.length === 0 ? (
            <Empty>Nenhum aluno cadastrado nesta unidade.</Empty>
          ) : (
            <TableWrapper>
              <table>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Email</th>
                    <th>Telefone</th>
                    <th>Nascimento</th>
                    <th>Batismo</th>
                    <th style={{ textAlign: "right" }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {people.map((person) => (
                    <tr key={person.id}>
                      <td>{person.name}</td>
                      <td>{person.email || "—"}</td>
                      <td>{person.phone || "—"}</td>
                      <td>{formatDate(person.date_of_birth)}</td>
                      <td>{formatDate(person.date_baptism)}</td>
                      <td>
                        <Actions>
                          <IconButton
                            title="Editar"
                            onClick={() => openEdit(person)}
                          >
                            <FiEdit2 />
                          </IconButton>
                          <IconButton
                            danger
                            title="Excluir"
                            onClick={() => handleDelete(person)}
                          >
                            <FiTrash2 />
                          </IconButton>
                        </Actions>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrapper>
          )}
        </Panel>
      </Content>

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? "Editar aluno" : "Novo aluno"}
        centered
        radius="lg"
        size={MODAL_MAX_WIDTH}
        fullScreen={isMobile}
        overlayProps={{ opacity: 0.55, blur: 3 }}
        transitionProps={{ transition: "pop" }}
        styles={modalStyles}
      >
        <Stack spacing="lg">
          <TextInput
            size="md"
            styles={fieldStyles}
            label="Nome"
            placeholder="Nome completo"
            value={form.name}
            onChange={(e) => setField("name", e.currentTarget.value)}
            withAsterisk
          />
          <SimpleGrid cols={2} spacing="lg" breakpoints={[{ maxWidth: 600, cols: 1 }]}>
            <TextInput
              size="md"
              styles={fieldStyles}
              label="Email"
              placeholder="email@exemplo.com"
              value={form.email}
              onChange={(e) => setField("email", e.currentTarget.value)}
            />
            <TextInput
              size="md"
              styles={fieldStyles}
              label="Telefone"
              placeholder="Somente números"
              value={form.phone}
              onChange={(e) => setField("phone", e.currentTarget.value)}
            />
          </SimpleGrid>
          <SimpleGrid cols={2} spacing="lg" breakpoints={[{ maxWidth: 600, cols: 1 }]}>
            <TextInput
              size="md"
              styles={fieldStyles}
              label="Data de nascimento"
              type="date"
              value={form.date_of_birth}
              onChange={(e) => setField("date_of_birth", e.currentTarget.value)}
            />
            <TextInput
              size="md"
              styles={fieldStyles}
              label="Data de batismo"
              type="date"
              value={form.date_baptism}
              onChange={(e) => setField("date_baptism", e.currentTarget.value)}
            />
          </SimpleGrid>
          <Group position="right" grow={isMobile} spacing="sm" mt="xs">
            <Button
              variant="default"
              styles={secondaryBtnStyles}
              onClick={() => setModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              color="indigo"
              styles={primaryBtnStyles}
              loading={isSaving}
              onClick={handleSave}
            >
              {editing ? "Salvar alterações" : "Cadastrar"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </PageWrapper>
  );
}

const Content = styled.div`
  flex: 1;
  padding: 1.6rem;
  max-width: 1100px;
  width: 100%;
  margin: 0 auto;

  @media (min-width: 768px) {
    padding: 2.4rem 3.2rem;
  }
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1.6rem;
`;

const Panel = styled.div`
  background: #fff;
  border: 1px solid #c0c0c9;
  border-radius: 1rem;
  padding: 0.8rem;
  overflow: hidden;
`;

const TableWrapper = styled.div`
  overflow-x: auto;

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th,
  td {
    font-size: 1.5rem;
    white-space: nowrap;
    padding: 1.4rem 1.6rem;
    text-align: left;
    border-bottom: 1px solid #eef0f4;
  }

  th {
    font-size: 1.3rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #7a7a80;
    font-weight: 700;
    background: #f7f8fb;
  }

  tbody tr {
    transition: background 0.12s;
  }
  tbody tr:hover {
    background: #f5f6fc;
  }
  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const Centered = styled.div`
  display: flex;
  justify-content: center;
  padding: 3.2rem;
`;

const Empty = styled.div`
  padding: 3.2rem;
  text-align: center;
  color: #7a7a80;
  font-size: 1.4rem;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.8rem;
  justify-content: flex-end;
`;

const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  height: 3.6rem;
  padding: 0 1.4rem;
  border-radius: 0.6rem;
  background-color: #3439a5;
  border: 0.1rem solid #2b2fa3;
  color: #fff;
  font-size: 1.4rem;
  font-weight: bold;
  cursor: pointer;

  svg {
    font-size: 1.8rem;
  }
`;

const IconButton = styled.button<{ danger?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.2rem;
  height: 3.2rem;
  border-radius: 0.6rem;
  border: 1px solid ${({ danger }) => (danger ? "#f1b0b7" : "#c0c0c9")};
  background: ${({ danger }) => (danger ? "#fdecea" : "#fff")};
  color: ${({ danger }) => (danger ? "#dc3545" : "#3439a5")};
  cursor: pointer;
  transition: filter 0.15s;

  svg {
    font-size: 1.6rem;
  }

  &:hover {
    filter: brightness(0.95);
  }
`;
